import LucideIcon from 'src/componentes/lucide-icon';
import Pagination from 'src/componentes/list/pagination';
import Spinner from 'src/componentes/spinner';
import css from 'src/utils/css';

const classes = css`
  .container {
    width: 100%;
  }

  .tableWrapper {
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .table {
    border-collapse: separate;
    border-spacing: 4px;
    min-width: 100%;
  }

  .headerCell {
    background: #ffffff;
    color: #667085;
    font-size: 12px;
    font-weight: 500;
    min-width: 124px;
    padding: 8px 10px;
    text-align: center;
    vertical-align: bottom;
  }

  .rowHeader {
    left: 0;
    min-width: 180px;
    position: sticky;
    text-align: left;
    z-index: 2;
  }

  .averageHeader {
    background: #f2f4f7;
    border-radius: 7px;
    color: #344054;
    font-weight: 700;
  }

  .rowLabel {
    background: #ffffff;
    color: #344054;
    font-size: 13px;
    font-weight: 500;
    left: 0;
    max-width: 220px;
    min-width: 180px;
    overflow: hidden;
    padding: 10px;
    position: sticky;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    z-index: 1;
  }

  .rowLabelContent {
    align-items: center;
    display: flex;
    min-width: 0;
  }

  .rowToggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #667085;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 22px;
    height: 22px;
    justify-content: center;
    margin: -3px 4px -3px -3px;
    padding: 0;
    width: 22px;
  }

  .rowTogglePlaceholder {
    flex: 0 0 22px;
    margin-right: 4px;
    width: 22px;
  }

  .rowLabelText {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell {
    background: #f2f4f7;
    border: 1px solid transparent;
    border-radius: 7px;
    color: #344054;
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    height: 42px;
    letter-spacing: 0;
    line-height: 120%;
    min-width: 124px;
    padding: 8px 10px;
    text-align: center;
  }

  .averageCell {
    border-width: 2px;
  }

  .legend {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 12px;
  }

  .legendItem {
    align-items: center;
    color: #475467;
    display: inline-flex;
    font-size: 12px;
    gap: 7px;
  }

  .legendSwatch {
    border: 1px solid rgba(16, 24, 40, 0.12);
    border-radius: 3px;
    display: inline-block;
    height: 14px;
    width: 24px;
  }

  .pagination {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  .customRow {
    background: #ffffff;
    padding: 4px 10px 8px;
  }

  .screenReaderOnly {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

const normalizeCell = (cell) => {
  if (cell && typeof cell === 'object') return cell;

  return { value: cell };
};

const defaultGetCell = (row, column) => row.cells?.[column.id];

function MapaCalor({
  ariaLabel,
  averageLabel,
  columns,
  emptyValue = '–',
  formatCellAriaLabel,
  formatValue = (value) => value,
  getCell = defaultGetCell,
  getCellAppearance = () => ({}),
  getCellTitle,
  getRowToggleLabel,
  hierarchicalRows = false,
  legend = [],
  onChangePage,
  onToggleRow,
  paginationCount = 0,
  paginationCurrentPage = 1,
  paginationPageSize,
  renderCustomRow,
  rowHeaderLabel,
  rows,
}) {
  const shouldPaginate =
    paginationPageSize > 0 && paginationCount > paginationPageSize;

  const renderCell = ({ cell: rawCell, column, isAverage, row }) => {
    const cell = normalizeCell(rawCell);
    const hasValue = cell.value != null;
    const appearance = hasValue ? getCellAppearance(cell.value, cell) : {};
    const formattedValue = hasValue
      ? formatValue(cell.value, cell)
      : emptyValue;
    const cellContext = {
      appearance,
      cell,
      column,
      formattedValue,
      hasValue,
      row,
    };
    const ariaCellLabel = formatCellAriaLabel?.(cellContext);
    const title = getCellTitle?.(cellContext) || ariaCellLabel;

    return (
      <td
        aria-label={ariaCellLabel}
        className={`${classes.cell} ${isAverage ? classes.averageCell : ''}`}
        key={column.id}
        style={{
          backgroundColor: appearance.backgroundColor,
          backgroundImage: appearance.backgroundImage,
          borderColor: appearance.borderColor,
          color: appearance.color,
        }}
        title={title}
      >
        {formattedValue}
        {appearance.statusLabel && (
          <span className={classes.screenReaderOnly}>
            {' '}
            — {appearance.statusLabel}
          </span>
        )}
      </td>
    );
  };

  const averageColumn = { id: '__average__', label: averageLabel };

  return (
    <div className={classes.container}>
      <div className={classes.tableWrapper}>
        <table aria-label={ariaLabel} className={classes.table}>
          <thead>
            <tr>
              <th
                className={`${classes.headerCell} ${classes.rowHeader}`}
                scope="col"
              >
                {rowHeaderLabel}
              </th>
              <th
                className={`${classes.headerCell} ${classes.averageHeader}`}
                scope="col"
              >
                {averageLabel}
              </th>
              {columns.map((column) => (
                <th
                  className={classes.headerCell}
                  key={column.id}
                  scope="col"
                  title={column.label}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              if (row.isCustom) {
                return (
                  <tr key={row.id ?? rowIndex}>
                    <td
                      className={classes.customRow}
                      colSpan={columns.length + 2}
                    >
                      {renderCustomRow?.(row)}
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={row.id ?? rowIndex}>
                  <th
                    className={classes.rowLabel}
                    scope="row"
                    title={row.label}
                  >
                    <div
                      className={classes.rowLabelContent}
                      style={{ paddingLeft: `${(row.level || 0) * 16}px` }}
                    >
                      {hierarchicalRows && row.hasChildren ? (
                        <button
                          aria-expanded={row.isExpanded}
                          aria-label={getRowToggleLabel?.(row, row.isExpanded)}
                          className={classes.rowToggle}
                          disabled={row.isLoading}
                          onClick={() => onToggleRow?.(row)}
                          type="button"
                        >
                          {row.isLoading ? (
                            <Spinner size={16} />
                          ) : (
                            <LucideIcon
                              icon={
                                row.isExpanded
                                  ? 'chevron-down'
                                  : 'chevron-right'
                              }
                              size={16}
                            />
                          )}
                        </button>
                      ) : (
                        hierarchicalRows && (
                          <span
                            aria-hidden="true"
                            className={classes.rowTogglePlaceholder}
                          />
                        )
                      )}
                      <span className={classes.rowLabelText}>{row.label}</span>
                    </div>
                  </th>
                  {renderCell({
                    cell: row.average,
                    column: averageColumn,
                    isAverage: true,
                    row,
                  })}
                  {columns.map((column) =>
                    renderCell({
                      cell: getCell(row, column),
                      column,
                      isAverage: false,
                      row,
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {legend.length > 0 && (
        <div className={classes.legend}>
          {legend.map((item) => (
            <span className={classes.legendItem} key={item.id}>
              <span
                aria-hidden="true"
                className={classes.legendSwatch}
                style={{
                  backgroundColor: item.backgroundColor,
                  backgroundImage: item.backgroundImage,
                }}
              />
              <span>{item.label}</span>
            </span>
          ))}
        </div>
      )}

      {shouldPaginate && (
        <div className={classes.pagination}>
          <Pagination
            allowPaging
            changeCurrentPage={onChangePage}
            changePageSize={() => {}}
            count={paginationCount}
            currentPage={paginationCurrentPage}
            pageSize={paginationPageSize}
            showPaginacaoSimples
          />
        </div>
      )}
    </div>
  );
}

export default MapaCalor;
